import React from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import {
  OfferList,
  CreateOffer,
  CreateShipping,
  Users,
  Login,
  Reset,
  Register,
  Settings,
  Offer,
  ShippingList,
  ViewShipping,
  LiteWms,
  LiteTms,
  LiteTmsWhs,
  LiteCustoms,
  SettingsLayout,
  SettingsKeyedOutlet,
  ShippingTabContent,
  ShippingCaseDefaultRedirect,
  CustomsLayout,
  CustomsDefaultRedirect,
  Customs,
  UnloadingLayout,
  UnloadingDefaultRedirect,
  Details,
  Warehausing,
  ShiftingAccordion,
  DepositaryAccordion,
  PickingAccordion,
  CargoReleaseAccordion,
  CargoDetailsLayout,
  Commodities,
  Voyage,
  DeliveryLayout,
  DeliveryDefaultRedirect,
  DeliveryList,
  WhsDeliveryList,
  Delivery,
  CommoditySheet,
  TMSLayoutWrapper,
  IncomeStatementLayout,
  IncomeStatement,
  LoadingOrderLayout,
  LoadingOrderWhsLayout,
  LoWhsSheet,
} from "../routes";

import Layout from "../layouts/main";
import Auth from "../layouts/auth";
import PrivateRoute from "../routes/privateRoute";
import { CancelFormProvider } from "@context/cancel-form-context";
import { TablePaginationProvider } from "@context/table-pagination-context";
import { RootRedirect } from "../routes/routRedirect";
import WithErrorHandling from "../hoc/with-error-handling";

export const router = createBrowserRouter([
  {
    element: <PrivateRoute />,
    children: [
      {
        path: "/",
        element: (
          <WithErrorHandling>
            <CancelFormProvider>
              <TablePaginationProvider>
                <Layout />
              </TablePaginationProvider>
            </CancelFormProvider>
          </WithErrorHandling>
        ),
        children: [
          {
            index: true,
            element: <RootRedirect />,
          },
          { path: "offers", element: <OfferList /> },
          { path: "offers/create", element: <CreateOffer /> },
          { path: "offers/:id", element: <Offer /> },
          { path: "offers/:id/update", element: <CreateOffer /> },
          {
            path: "offers/:id/shipping-case/create",
            element: <CreateShipping />,
          },
          {
            path: "offers/:oid/shipping-case/:sid",
            element: <ViewShipping />,
            children: [
              {
                index: true,
                element: <ShippingCaseDefaultRedirect />,
              },

              {
                path: "customs",
                element: <CustomsLayout />,
                children: [
                  { index: true, element: <CustomsDefaultRedirect /> },
                  { path: "control-protocols", element: <Customs /> },
                  { path: "radiometric", element: <Customs /> },
                  { path: "bills", element: <Customs /> },
                  { path: "bills/create", element: <Customs /> },
                  { path: "bills/:id", element: <Customs /> },
                ],
              },

              {
                path: "unloading",
                element: <UnloadingLayout />,
                children: [
                  { index: true, element: <UnloadingDefaultRedirect /> },
                  { path: "details", element: <Details /> },
                  { path: "warehousing", element: <Warehausing /> },
                  { path: "deposit-warehouse", element: <ShiftingAccordion /> },
                  {
                    path: "depositary-change",
                    element: <DepositaryAccordion />,
                  },
                  { path: "picking-service", element: <PickingAccordion /> },
                  { path: "fcr", element: <CargoReleaseAccordion /> },
                ],
              },

              {
                path: "delivery",
                element: <DeliveryLayout />,
                children: [
                  { index: true, element: <DeliveryDefaultRedirect /> },
                  { path: "delivery-orders", element: <Delivery /> },
                  { path: "balance-volume", element: <Delivery /> },
                  { path: "deposit-warehouse", element: <Delivery /> },
                  { path: "whs-delivery-orders", element: <Delivery /> },
                  {
                    path: "delivery-orders/:id",
                    element: <Delivery />,
                  },
                  { path: "delivery-orders/create", element: <Delivery /> },
                  {
                    path: "whs-delivery-orders/:id",
                    element: <Delivery />,
                  },
                  { path: "whs-delivery-orders/create", element: <Delivery /> },
                ],
              },

              {
                path: "income-statement",
                element: <IncomeStatementLayout />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="selected-services" replace />,
                  },
                  {
                    path: "selected-services",
                    element: <IncomeStatement />,
                  },
                  {
                    path: "canceled-services",
                    element: <IncomeStatement />,
                  },
                  {
                    path: "selected-services/update",
                    element: <IncomeStatement />,
                  },
                  {
                    path: "selected-services/:customerId",
                    element: <IncomeStatement />,
                  },
                  {
                    path: "canceled-services/update",
                    element: <IncomeStatement />,
                  },
                ],
              },

              {
                path: "cargo-details",
                element: <CargoDetailsLayout />,
                children: [
                  { index: true, element: <Navigate to="commodity" replace /> },
                  { path: "commodity", element: <Commodities /> },
                  { path: "voyage", element: <Voyage /> },
                ],
              },

              {
                path: "tms",
                element: <TMSLayoutWrapper />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="loading-orders" replace />,
                  },
                  { path: "loading-orders", element: <LoadingOrderLayout /> },
                  {
                    path: "loading-orders/:order",
                    element: <CommoditySheet />,
                  },
                  {
                    path: "whs-loading-orders",
                    element: <LoadingOrderWhsLayout />,
                  },
                  {
                    path: "whs-loading-orders/:order",
                    element: <LoWhsSheet />,
                  },
                ],
              },

              { path: ":slug", element: <ShippingTabContent /> },
            ].filter(Boolean) as any,
          },
          {
            path: "offers/:oid/shipping-case/:sid/update",
            element: <CreateShipping />,
          },
          { path: "users", element: <Users /> },
          { path: "shippings", element: <ShippingList /> },
          { path: "wms", element: <LiteWms /> },
          { path: "tms", element: <LiteTms /> },
          { path: "tms-whs", element: <LiteTmsWhs /> },
          { path: "customs", element: <LiteCustoms /> },
          { path: "delivery", element: <DeliveryList /> },
          { path: "whs-delivery", element: <WhsDeliveryList /> },
          {
            path: "settings",
            element: <SettingsLayout />,
            children: [
              {
                index: true,
                element: <Navigate to="assigned-offices" replace />,
              },
              {
                element: <SettingsKeyedOutlet />,
                children: [
                  { path: ":entityKey/:id/:mode", element: <Settings /> },
                  { path: ":entityKey/create", element: <Settings /> },
                  { path: ":entityKey", element: <Settings /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/",
    element: <Auth />,
    children: [
      { path: "login", element: <Login /> },
      { path: "reset", element: <Register /> },
      { path: "register", element: <Register /> },
      { path: "reset-password", element: <Reset /> },
    ],
  },
]);
